/**
 * Hook for handling keyboard shortcuts
 */

import { useEffect, useCallback } from "react"

interface UseKeyboardShortcutOptions {
  key: string
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  callback: () => void
  enabled?: boolean
}

export const useKeyboardShortcut = ({
  key,
  ctrlKey = false,
  metaKey = false,
  shiftKey = false,
  callback,
  enabled = true,
}: UseKeyboardShortcutOptions): void => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return

      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      const isMatch =
        event.key.toLowerCase() === key.toLowerCase() &&
        event.ctrlKey === ctrlKey &&
        event.metaKey === metaKey &&
        event.shiftKey === shiftKey

      // Also support either Cmd or Ctrl for cross-platform compatibility
      const isCrossplatformMatch =
        event.key.toLowerCase() === key.toLowerCase() &&
        (event.metaKey || event.ctrlKey) &&
        !shiftKey

      if (isMatch || (key === "k" && isCrossplatformMatch)) {
        event.preventDefault()
        callback()
      }
    },
    [key, ctrlKey, metaKey, shiftKey, callback, enabled]
  )

  useEffect(() => {
    if (enabled) {
      window.addEventListener("keydown", handleKeyDown)
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleKeyDown, enabled])
}




